import { Box, Flex, Skeleton, SkeletonCircle } from '@chakra-ui/react';
import type { FlexProps } from '@chakra-ui/react';
import type React from 'react';
import FaceliftText from '@/components/Atoms/FaceliftText';
import Show from '@/components/Molecules/Show';

export interface ScoreCardProps extends FlexProps {
  /** Title above the value, e.g., "Order Shipped" */
  title: string;
  /** Main numeric value to display */
  value: number | string;
  /** Subtext below the main content, e.g., "This Month" */
  subtitle?: string;
  /** Icon element displayed in the circular badge (passed from outside) */
  icon?: React.ReactNode;
  /** Loading state */
  isLoading?: boolean;
  /** Optional: Custom icon circle size */
  iconSize?: string;
  /** Optional: Custom value font size */
  valueFontSize?: string;
  /** Optional: Custom icon font size */
  iconFontSize?: string;
  /** Optional: Center align content */
  centerAlign?: boolean;
}

const ScoreCard: React.FC<ScoreCardProps> = ({
  title,
  value,
  icon,
  isLoading = false,
  ...rest
}) => {  
  // Extract custom size props from rest
  const { iconSize, valueFontSize, iconFontSize, centerAlign, ...flexProps } = rest as any;
  const finalIconSize = iconSize || '50px';
  const finalValueFontSize = valueFontSize || '2xl';
  const finalIconFontSize = iconFontSize || 'xl';
  const alignment = centerAlign ? 'center' : { base: 'center', sm: 'flex-start' };
  
  return (
    <Flex
      direction="column"
      border="1px solid"
      borderColor={'gray.400'}
      borderRadius="5px"
      p={4}
      _dark={{ bg: 'darkModeBgColor' }}
      _light={{ bg: 'white' }}
      gap={3}
      flex={{ base: '1', sm: '0 0 auto' }}
      {...flexProps}
      display="flex"
      justifyContent="center"
    >
      <Flex align="center" gap={2.5} justifyContent={alignment}>
        <SkeletonCircle
          size={finalIconSize}
          loading={isLoading}
          _dark={{ bg: isLoading ? 'gray.700' : 'transparent' }}
          _light={{ bg: isLoading ? 'gray.200' : 'transparent' }}
        >
          <Box
            w={finalIconSize}
            h={finalIconSize}
            borderRadius="full"
            display="flex"
            alignItems="center"
            justifyContent="center"
            bg="brand.solidGradient"
            color="white"
          >
            <Box
              fontSize={finalIconFontSize}
              display="flex"
              alignItems="center"
              justifyContent="center"
              css={{
                '& svg': {
                  width: '1em',
                  height: '1em',
                }
              }}
            >
              {icon}
            </Box>
          </Box>
        </SkeletonCircle>
        <Box flex={centerAlign ? 'none' : { base: '0 0 auto', sm: '1' }} textAlign={centerAlign ? 'left' : 'left'}>
          <Skeleton
            loading={isLoading}
            height={isLoading ? "16px" : "auto"}
            mb={1}
            _dark={{ bg: isLoading ? 'gray.700' : 'transparent' }}
            _light={{ bg: isLoading ? 'gray.200' : 'transparent' }}
          >
            <FaceliftText fontSize="sm" fontWeight="semibold" _dark={{ color: 'white' }} _light={{ color: 'black' }}>
              {title}
            </FaceliftText>
          </Skeleton>
          <Skeleton
            loading={isLoading}
            height={isLoading ? "28px" : "auto"}
            _dark={{ bg: isLoading ? 'gray.700' : 'transparent' }}
            _light={{ bg: isLoading ? 'gray.200' : 'transparent' }}
          >
            <FaceliftText fontSize={finalValueFontSize} fontWeight="bold" mt={-1} _dark={{ color: 'white' }} _light={{ color: 'oceanBlue' }}>
              {value}
            </FaceliftText>
          </Skeleton>
        </Box>
      </Flex>
    </Flex>
  );
};

export default ScoreCard;
